import Link from 'next/link';
import {
  ArrowSmallRightIcon,
  InformationCircleIcon,
  TicketIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  formatDate,
  slugify,
  parseRecurrenceRule,
  getHumanReadableRecurrence,
  cleanNPSContent,
} from '@/lib/util';
import Image from 'next/image';
import clsx from 'clsx';
import SmallInfo from '@/components/SmallInfo';
import HeadingStyle from '@/components/HeadingStyle';
import ClientOnly from '@/components/ClientOnly';
import { useState } from 'react';

export default function EventItem({ event, isListView, eventHeadingLevel }) {
  const [imgError, setImgError] = useState(false);
  const placeholderImage = '/placeholder/placeholder.png';

  // Support both camelCase (database) and lowercase (legacy JSON) field names
  let category = event && event.category,
    dates = event && event.dates,
    dateStart = event && (event.dateStart || event.datestart),
    dateEnd = event && (event.dateEnd || event.dateend),
    description = event && event.description,
    feeInfo = event && (event.feeInfo || event.feeinfo),
    recurrencerule = event && (event.recurrenceRule || event.recurrencerule),
    registerUrl = event && (event.regresUrl || event.regresurl),
    infoUrl = event && (event.infoUrl || event.infourl),
    firstImage = event?.images?.[0]?.url
      ? `https://www.nps.gov${event?.images?.[0]?.url}`
      : null,
    //eventUrl = registerUrl ? registerUrl : infoUrl,
    // Use slug if available, otherwise generate from title
    eventSlug = event && (event.slug || slugify(event.title)),
    eventUrl = event && `/event/${eventSlug}`,
    recurrenceDateStart =
      event && (event.recurrenceDateStart || event.recurrencedatestart),
    recurrenceDateEnd =
      event && (event.recurrenceDateEnd || event.recurrencedateend),
    parkFullName = event && (event.parkFullName || event.parkfullname),
    times = event && event.times,
    timeEnd = times?.timeEnd || times?.timeend,
    timeStart = times?.timeStart || times?.timestart,
    title = event && event.title,
    types = event && event.types,
    isRegistrationRequired =
      event && (event.isRegresRequired || event.isregresrequired) === 'true',
    isRecurring = event && (event.isRecurring || event.isrecurring),
    parkCode = event && event.parkCode,
    image = imgError ? placeholderImage : firstImage || placeholderImage,
    isPlaceholder = image === placeholderImage;

  // Parse recurrence rule for better display
  const parsedRecurrenceRule = recurrencerule
    ? parseRecurrenceRule(recurrencerule)
    : null;
  const humanReadableRecurrence = parsedRecurrenceRule
    ? getHumanReadableRecurrence(parsedRecurrenceRule)
    : 'Repeating Event';

  // console.log('event',event);

  if (!event) return null;

  return (
    <article className="bg-white h-full rounded border border-brown-100/10 overflow-hidden text-brown-100 text-sm transition shadow-sm hover:shadow-md">
      <div
        className={`flex justify-between h-full ${isListView ? 'flex-row' : 'flex-col gap-0'}`}
      >
        <div className={`flex flex-col ${isListView ? 'md:w-4/6' : 'w-full'}`}>
          {/*  py-4 px-4 md:py-5 md:px-5 */}
          <div className={`flex ${isListView ? '' : 'flex-col'}`}>
            <div
              className={`${isListView ? 'h-full w-[300px]' : 'w-full h-[200px]'}`}
            >
              <div className="relative h-full block">
                {/* {formattedDate && (
                                             <time dateTime={dateStart} className={`absolute top-0 left-0 w-[100px] h-fit py-3 px-2 bg-green-100 text-white font-serif text-center mr-3 z-1 rounded-br-xs ${formattedDate.isOngoing ? 'bg-orange-500' : ''}`}>
                                                 {!formattedDate.isOngoing && (
                                                     <span className="block text-xl md:text-2xl -mt-2 font-light">{formattedDate.subText}</span>
                                                 )}
                                                 {formattedDate.isOngoing && (
                                                     <span className="block text-xs leading-tight -mt-1">
                                                        <span className="block">{formattedDate.displayText}</span>
                                                        <span className="block">{formattedDate.subText}</span>
                                                     </span>
                                                 )}
                                             </time>
                                         )} */}
                <p
                  className={`text-xxs font-bold absolute bottom-0 z-1 bg-white py-1 px-3 text-brown-100/70
                                         ${isListView ? 'right-0 rounded-tl-xs' : 'left-0 rounded-tr-xs'}`}
                >
                  {category}
                </p>
                <Image
                  src={image}
                  alt={parkFullName || 'Event'}
                  width={500}
                  height={500}
                  className={`object-cover w-full h-full z-0 aspect-square ${isPlaceholder ? '' : 'object-top'} ${isListView ? 'absolute top-0' : ''}`}
                  onError={() => setImgError(true)}
                  unoptimized={image.startsWith('https://')}
                />
              </div>
            </div>

            <div
              className={`basis-full ${isListView ? 'md:py-5 md:px-5' : 'p-5'}`}
            >
              <Link
                href={`/park/${slugify(parkFullName)}`}
                className="text-xxs uppercase font-black text-brown-100/80"
              >
                {parkFullName}
              </Link>
              <HeadingStyle
                className="text-lg font-normal leading-tight text-green-100 md:text-xl my-0 mt-2 w-full"
                level={eventHeadingLevel}
              >
                {eventUrl ? (
                  <a href={`${eventUrl}`} target="_blank" rel="noreferrer">
                    {title}
                  </a>
                ) : (
                  title
                )}
              </HeadingStyle>

              {/* Show ongoing status */}
              {/* {formattedDate?.isOngoing && (
                                         <div className="flex items-center mt-2">
                                             <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                 <span className="w-2 h-2 bg-orange-400 rounded-full mr-2 animate-pulse"></span>
                                                 Ongoing Event
                                             </span>
                                         </div>
                                     )} */}

              {isRegistrationRequired ? (
                <p className="font-sans text-xs mt-2">Registration Required</p>
              ) : null}

              {registerUrl || infoUrl ? (
                <div className="mt-4 text-xs leading-8">
                  {infoUrl ? (
                    <a
                      href={infoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex"
                    >
                      <InformationCircleIcon
                        width={20}
                        className="mr-2 -mt-1"
                      />{' '}
                      <span>More information</span>
                    </a>
                  ) : null}
                  {registerUrl ? (
                    <>
                      <a
                        href={registerUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex"
                      >
                        <TicketIcon width={20} className="mr-2 -mt-1" />{' '}
                        <span>Buy tickets</span>
                      </a>
                    </>
                  ) : null}
                </div>
              ) : null}

              {types ? (
                <ul className={`flex flex-wrap mt-4`}>
                  {types.map((type, i) => (
                    <li key={`${type}-${i}`}>
                      <p
                        className={`text-xxs my-1 py-1 pb-1 px-3 bg-gray rounded-full mr-2`}
                      >
                        {type}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : null}

              <ClientOnly>
                {description && description.length > 120 ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: cleanNPSContent(
                        description.slice(0, 120) +
                          `... <a href="${eventUrl}">Read more</a>`
                      ),
                    }}
                    className="mt-2 text-xs"
                  ></div>
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: cleanNPSContent(description),
                    }}
                    className="mt-2 text-xs"
                  ></div>
                )}
              </ClientOnly>
            </div>
          </div>
        </div>
        <div
          className={`${isListView ? 'gap-4 flex flex-row justify-between w-2/6 border-l border-brown-100/10' : 'p-5 border-t border-brown-100/10'}`}
        >
          <div
            className={`flex flex-col gap-4 ${isListView ? 'md:py-5 md:px-5 w-full' : ''}`}
          >
            {isRecurring ? (
              <div
                className={`flex uppercase text-xxs font-bold items-center text-brown-100/60`}
              >
                <ArrowPathIcon width="18" className="mr-2 min-w-fit" />{' '}
                <span className="w-full">{humanReadableRecurrence}</span>
              </div>
            ) : null}

            {/* Date display - static dates only to prevent hydration mismatch */}
            <div>
              <h3 className="uppercase text-xxs font-sans font-black text-green-100 mb-1">
                Date(s)
              </h3>
              <p className="text-xs">
                {recurrenceDateStart && recurrenceDateEnd ? (
                  <>
                    {formatDate(recurrenceDateStart)} -{' '}
                    {formatDate(recurrenceDateEnd)}
                  </>
                ) : recurrenceDateStart ? (
                  formatDate(recurrenceDateStart)
                ) : dateStart ? (
                  formatDate(dateStart)
                ) : null}
              </p>
            </div>

            {feeInfo ? <SmallInfo title="Fees">{feeInfo}</SmallInfo> : null}
            {timeStart || timeEnd ? (
              <div>
                <h3 className="uppercase text-xxs font-sans font-black text-green-100 mb-1">
                  Time
                </h3>
                <p className="text-xs">
                  {timeStart ? timeStart : null}
                  {timeStart && timeEnd ? ' - ' : null}
                  {timeEnd ? timeEnd : null}
                </p>
              </div>
            ) : null}
          </div>
          {eventUrl && isListView ? (
            <a
              className={`flex self-center justify-center px-3 bg-green-100 text-white ${isListView ? 'w-20 h-full' : 'w-full h-10'}`}
              href={`${eventUrl}`}
              target="_blank"
              rel="noreferrer"
            >
              <span className="sr-only">View event</span>
              <ArrowSmallRightIcon width={20} />
            </a>
          ) : null}
        </div>
        {eventUrl && !isListView ? (
          <a
            className={`flex self-center justify-center px-3 bg-green-100 text-white ${isListView ? 'w-20' : 'w-full h-10'}`}
            href={`${eventUrl}`}
            target="_blank"
            rel="noreferrer"
          >
            <span className="sr-only">View event</span>
            <ArrowSmallRightIcon width={20} />
          </a>
        ) : null}
      </div>
    </article>
  );
}
